import { FeatureFlag, FeatureFlagSource } from '@core/feature-flags/feature-flag-enums';
import { FeatureFlagStatus } from '@core/feature-flags/feature-flags';
import { featureFlagLabels } from '@core/feature-flags/feature-flag-labels';

export type FeatureFlagFormValues = Partial<Record<FeatureFlag, boolean>>;

export type FeatureFlagPartitions<T> = {
  enabled: T[];
  disabled: T[];
};

/** Only flags with a label mapping are ever shown, whether user-controllable or server-managed. */
export const mappedFlags = Object.keys(featureFlagLabels).map(Number) as FeatureFlag[];

export const getUserControllableFlags = (
  flagStatuses: Record<FeatureFlag, FeatureFlagStatus>
): FeatureFlag[] =>
  mappedFlags.filter((flag) => flagStatuses[flag].source !== FeatureFlagSource.Server);

export const partitionByValue = <T>(entries: [T, boolean][]): FeatureFlagPartitions<T> =>
  entries.reduce<FeatureFlagPartitions<T>>(
    (partitions, [key, value]) => {
      (value ? partitions.enabled : partitions.disabled).push(key);
      return partitions;
    },
    { enabled: [], disabled: [] }
  );

export const getServerFlagsByValue = (
  flagStatuses: Record<FeatureFlag, FeatureFlagStatus>
): FeatureFlagPartitions<FeatureFlag> =>
  partitionByValue(
    mappedFlags
      .filter((flag) => flagStatuses[flag].source === FeatureFlagSource.Server)
      .map((flag): [FeatureFlag, boolean] => [flag, flagStatuses[flag].value])
  );

export const getUnknownServerFlagsByValue = (
  unknownServerFlags: Record<number, boolean>
): FeatureFlagPartitions<number> =>
  partitionByValue(
    Object.entries(unknownServerFlags).map(([key, value]): [number, boolean] => [
      Number(key),
      value,
    ])
  );

export const getFeatureFlagFormDefaultValues = (
  userControllableFlags: FeatureFlag[],
  flagStatuses: Record<FeatureFlag, FeatureFlagStatus>
): FeatureFlagFormValues =>
  userControllableFlags.reduce<FeatureFlagFormValues>((values, flag) => {
    values[flag] = flagStatuses[flag].value;
    return values;
  }, {});
